const API_BASE_URL = 'https://dtech-pdf-api-1.onrender.com';

const handleApiError = async (response: Response): Promise<Error> => {
  try {
    const errorData = await response.json();
    let errorMessage = errorData.detail;
    if (typeof errorMessage === 'object' && errorMessage !== null) {
      errorMessage = JSON.stringify(errorMessage);
    }
    return new Error(errorMessage || `HTTP error! status: ${response.status}`);
  } catch (e) {
    return new Error(`HTTP error! status: ${response.status}`);
  }
};

const makeApiRequestWithProgress = <T>(
  url: string,
  formData: FormData,
  onProgress: (percentage: number) => void,
  responseType: 'blob' | 'json'
): Promise<T> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);

    if (responseType === 'blob') {
      xhr.responseType = 'blob';
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentage = (event.loaded / event.total) * 100;
        onProgress(percentage);
      }
    };

    xhr.onload = () => {
      onProgress(100); // Ensure it completes to 100
      if (xhr.status >= 200 && xhr.status < 300) {
        if (responseType === 'json') {
          try {
            const jsonResponse = JSON.parse(xhr.responseText);
            resolve(jsonResponse);
          } catch (e) {
            reject(new Error('Failed to parse JSON response.'));
          }
        } else {
          resolve(xhr.response);
        }
      } else {
        if (responseType === 'blob' && xhr.response instanceof Blob) {
            xhr.response.text()
              .then(text => {
                const mockResponse = new Response(text, {
                  status: xhr.status,
                  statusText: xhr.statusText,
                  headers: { 'Content-Type': 'application/json' },
                });
                handleApiError(mockResponse).then(reject);
              })
              .catch(() => {
                reject(new Error(`HTTP error! status: ${xhr.status}`));
              });
        } else {
            // For 'json' responseType, or if blob reading fails, fallback to responseText
            const mockResponse = new Response(xhr.responseText, {
              status: xhr.status,
              statusText: xhr.statusText,
              headers: { 'Content-Type': 'application/json' },
            });
            handleApiError(mockResponse).then(reject);
        }
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network request failed.'));
    };

    xhr.send(formData);
  });
};

export const mergePdfs = async (files: File[], onProgress: (p: number) => void): Promise<Blob> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });
  return makeApiRequestWithProgress<Blob>(`${API_BASE_URL}/pdf/merge`, formData, onProgress, 'blob');
};

export const splitPdf = async (file: File, onProgress: (p: number) => void): Promise<Blob> => {
  const formData = new FormData();
  formData.append('file', file);
  return makeApiRequestWithProgress<Blob>(`${API_BASE_URL}/pdf/split`, formData, onProgress, 'blob');
};

export const extractTextFromPdf = async (file: File, onProgress: (p: number) => void): Promise<{text: string}> => {
    const formData = new FormData();
    formData.append('file', file);
    const data = await makeApiRequestWithProgress<{ text: string }>(`${API_BASE_URL}/pdf/extract-text`, formData, onProgress, 'json');
    if (data && typeof data.text === 'string') {
        return data;
    }
    throw new Error('Unexpected response format from text extraction service.');
};


export const resumeDocument = async (file: File, language: string, onProgress: (p: number) => void): Promise<{ filename: string; summary: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const url = `${API_BASE_URL}/document/resume-document?language=${encodeURIComponent(language)}`;
  return makeApiRequestWithProgress<{ filename: string; summary: string }>(url, formData, onProgress, 'json');
};

export const convertPdfToDocx = async (file: File, onProgress: (p: number) => void): Promise<Blob> => {
  const formData = new FormData();
  formData.append('file', file);
  return makeApiRequestWithProgress<Blob>(`${API_BASE_URL}/pdf/convert/pdf-to-docx`, formData, onProgress, 'blob');
};

export const convertDocxToPdf = async (file: File, onProgress: (p: number) => void): Promise<Blob> => {
  const formData = new FormData();
  formData.append('file', file);
  return makeApiRequestWithProgress<Blob>(`${API_BASE_URL}/pdf/convert/docx-to-pdf`, formData, onProgress, 'blob');
};

export const convertPptxToPdf = async (file: File, onProgress: (p: number) => void): Promise<Blob> => {
  const formData = new FormData();
  formData.append('file', file);
  return makeApiRequestWithProgress<Blob>(`${API_BASE_URL}/pdf/convert/pptx-to-pdf`, formData, onProgress, 'blob');
};

export const convertImageToPdf = async (files: File[], onProgress: (p: number) => void): Promise<Blob> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });
  return makeApiRequestWithProgress<Blob>(`${API_BASE_URL}/pdf/convert/image-to-pdf`, formData, onProgress, 'blob');
};

export const compressPdf = async (file: File, level: 'low' | 'medium' | 'high', onProgress: (p: number) => void): Promise<Blob> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('level', level);
  return makeApiRequestWithProgress<Blob>(`${API_BASE_URL}/pdf/compress`, formData, onProgress, 'blob');
};


export const protectPdf = async (file: File, password: string, onProgress: (p: number) => void): Promise<Blob> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('password', password);
  return makeApiRequestWithProgress<Blob>(`${API_BASE_URL}/pdf/protect`, formData, onProgress, 'blob');
};

export const extractImages = async (file: File, onProgress: (p: number) => void): Promise<Blob> => {
  const formData = new FormData();
  formData.append('file', file);
  return makeApiRequestWithProgress<Blob>(`${API_BASE_URL}/document/extract-images`, formData, onProgress, 'blob');
};

export const addWatermark = async (
  document: File,
  watermark: File,
  position: string,
  size: number,
  onProgress: (p: number) => void
): Promise<Blob> => {
  const formData = new FormData();
  formData.append('document', document);
  formData.append('watermark', watermark);
  const url = `${API_BASE_URL}/pdf/watermark?position=${encodeURIComponent(position)}&size=${size}`;
  return makeApiRequestWithProgress<Blob>(url, formData, onProgress, 'blob');
};